import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  PERFIL_COPY,
  PerfilSeccionId,
} from '../../../data/perfil-microempresario.content';
import { UserService } from '../../../../services/user.service';
import { isPerfilJuridica } from '../../perfil-tipo.util';
import { PerfilSeccionAccesoService } from '../../progress/perfil-seccion-acceso.service';
import {
  PERFIL_PASO_QUERY,
  PERFIL_SECTION_ROUTES,
} from '../../progress/perfil-section-nav';
import { PerfilProgressService } from '../../progress/perfil-progress.service';
import { PerfilSegmentoService } from '../../segmento/perfil-segmento.service';
import { AnalyticsTagService } from 'src/app/services/analytics-tag.service';

export interface PerfilStepItem {
  id: PerfilSeccionId;
  titulo: string;
  icon: 'face' | 'store' | 'attach_money';
  progreso: number;
  checkpoints: number;
}

/**
 * Set de pasos compartido (Sobre ti / negocio / ingresos).
 * Lee avance desde PerfilProgressService (o @Input pasos si se inyecta).
 */
@Component({
  selector: 'app-perfil-steps-strip',
  templateUrl: './perfil-steps-strip.component.html',
  styleUrls: ['./perfil-steps-strip.component.scss'],
  standalone: false,
})
export class PerfilStepsStripComponent implements OnInit, OnDestroy {
  @Input() activa: PerfilSeccionId = 'sobre-ti';

  /**
   * Override opcional. Si es null, se usa el snapshot del servicio de avance.
   */
  @Input() pasos: PerfilStepItem[] | null = null;

  abierto = false;

  readonly copy = PERFIL_COPY;

  private readonly iconMap: Record<PerfilStepItem['icon'], string> = {
    face: 'assets/img/perfil/icon-sobre-ti.svg',
    store: 'assets/img/perfil/icon-sobre-negocio.svg',
    attach_money: 'assets/img/perfil/icon-sobre-ingresos.svg',
  };

  private readonly iconActiveMap: Record<PerfilStepItem['icon'], string> = {
    face: 'assets/img/perfil/icon-sobre-ti-active.svg',
    store: 'assets/img/perfil/icon-sobre-negocio-active.svg',
    attach_money: 'assets/img/perfil/icon-sobre-ingresos-active.svg',
  };

  private servicePasos: PerfilStepItem[] = [];
  private progressSub?: Subscription;

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly progress: PerfilProgressService,
    private readonly segmentoService: PerfilSegmentoService,
    private readonly seccionAcceso: PerfilSeccionAccesoService,
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly analyticsTag: AnalyticsTagService
  ) {}

  ngOnInit(): void {
    this.applySnapshot(this.progress.getSnapshot());
    this.progressSub = this.progress.snapshot$.subscribe((snap) =>
      this.applySnapshot(snap)
    );
  }

  ngOnDestroy(): void {
    this.progressSub?.unsubscribe();
  }

  get items(): PerfilStepItem[] {
    return this.pasos?.length ? this.pasos : this.servicePasos;
  }

  get introTitle(): string {
    if (isPerfilJuridica(this.userService.getUser()?.tipoPersona)) {
      return this.copy.footerBar.titleJuridica ?? this.copy.footerBar.title;
    }
    return this.copy.footerBar.title;
  }

  get introSubtitle(): string {
    const juridica = isPerfilJuridica(this.userService.getUser()?.tipoPersona);
    if (juridica && this.activa === 'sobre-ti') {
      return this.copy.footerBar['sobre-ti-juridica'];
    }
    if (juridica && this.activa === 'sobre-ingresos') {
      return this.copy.footerBar['sobre-ingresos-juridica'];
    }
    if (this.activa === 'sobre-ingresos' && this.segmentoService.esExpress) {
      return this.copy.footerBar['sobre-ingresos-express'];
    }
    return this.copy.footerBar[this.activa];
  }

  get progresoGlobal(): number {
    if (this.pasos?.length) {
      const sum = this.pasos.reduce((acc, p) => acc + (p.progreso ?? 0), 0);
      return Math.round(sum / this.pasos.length);
    }
    return this.progress.getSnapshot().globalPercent;
  }

  get seccionesCompletas(): number {
    if (this.pasos?.length) {
      return this.pasos.filter((p) => (p.progreso ?? 0) >= 100).length;
    }
    return this.progress.getSnapshot().seccionesCompletas;
  }

  iconSrc(icon: PerfilStepItem['icon'], active = false): string {
    return active ? this.iconActiveMap[icon] : this.iconMap[icon];
  }

  isActiva(paso: PerfilStepItem): boolean {
    return paso.id === this.activa;
  }

  isCompleta(paso: PerfilStepItem): boolean {
    return (paso.progreso ?? 0) >= 100;
  }

  /** Hover + CTA: secciones visitables que no son la que se está trabajando. */
  isInteractive(paso: PerfilStepItem): boolean {
    if (this.isActiva(paso)) {
      return false;
    }
    return this.canNavigate(paso);
  }

  canJumpSteps(paso: PerfilStepItem): boolean {
    return this.isActiva(paso) || this.canNavigate(paso);
  }

  accionLabel(paso: PerfilStepItem): string {
    if (this.isCompleta(paso)) {
      return this.copy.acciones.editar;
    }
    if ((paso.progreso ?? 0) > 0) {
      return this.copy.acciones.retomar;
    }
    return this.copy.acciones.empezar;
  }

  onStepClick(paso: PerfilStepItem): void {
    if (!this.isInteractive(paso)) {
      return;
    }
    this.navigateTo(paso.id);
  }

  onStepKeydown(event: Event, paso: PerfilStepItem): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('.perfil-progress-section__marker')) {
      return;
    }
    this.onStepClick(paso);
  }

  onMarkerClick(paso: PerfilStepItem, index: number): void {
    if (!this.canJumpSteps(paso)) {
      return;
    }
    this.navigateTo(paso.id, index);
  }

  progresoLabel(progreso: number): string {
    const n = Math.max(0, Math.min(100, Math.round(progreso ?? 0)));
    return `${n}% ${this.copy.hub.completadoSuffix}`;
  }

  toggle(): void {
    this.analyticsTag.trackCtaClick({
      ctaLabel: this.copy.stepsStrip.progresoToggle,
      ctaSection: 'perfil-steps-strip',
      ctaComponent: 'perfil-steps-strip',
      ctaElementId: 'btn-perfil-steps-progreso',
      hu: '8099',
    });
    this.abierto = !this.abierto;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.abierto) {
      return;
    }
    const target = event.target as Node | null;
    if (target && !this.host.nativeElement.contains(target)) {
      this.abierto = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.abierto = false;
  }

  private canNavigate(paso: PerfilStepItem): boolean {
    if (this.isCompleta(paso)) {
      return true;
    }
    return this.seccionAcceso.canAccess(paso.id);
  }

  private navigateTo(seccionId: PerfilSeccionId, pasoIndex?: number): void {
    const url = PERFIL_SECTION_ROUTES[seccionId];
    if (!url) {
      return;
    }
    const qs =
      pasoIndex != null ? `?${PERFIL_PASO_QUERY}=${pasoIndex}` : '';
    void this.router.navigateByUrl(`${url}${qs}`);
  }

  private applySnapshot(
    snap: ReturnType<PerfilProgressService['getSnapshot']>
  ): void {
    const juridica = isPerfilJuridica(this.userService.getUser()?.tipoPersona);
    const iconById: Record<PerfilSeccionId, PerfilStepItem['icon']> = {
      'sobre-ti': juridica ? 'store' : 'face',
      'sobre-negocio': 'store',
      'sobre-ingresos': 'attach_money',
    };
    this.servicePasos = snap.sections.map((s) => {
      const sec = PERFIL_COPY.secciones[s.id];
      const titulo =
        juridica && s.id === 'sobre-ti' && 'tituloJuridica' in sec
          ? (sec.tituloJuridica as string) ?? sec.titulo
          : sec.titulo;
      return {
        id: s.id,
        titulo,
        icon: iconById[s.id],
        progreso: Object.keys(this.progress.getSectionValues(s.id)).length
          ? s.percent
          : this.seccionAcceso.sectionPercent(s.id),
        checkpoints: s.checkpoints,
      };
    });
  }
}
